import { useCallback, useEffect, useState } from 'react';

import { logger } from 'common/services';

type Props = { key: string } | string;

export const useLocalStorage = <T = unknown>(props: Props) => {
  const key = typeof props === 'string' ? props : props.key;
  const [data, setData] = useState<T | undefined>(() => {
    if (typeof window === 'undefined') return undefined;

    // Guarded as well as the parse: reading `localStorage` itself throws where storage access is
    // denied (a sandboxed frame with an opaque origin, some privacy modes). Every shared `Alert`
    // calls this hook, including the ErrorBoundary fallback, which must never throw while rendering.
    let storedDataString: string | null;
    try {
      storedDataString = window.localStorage.getItem(key);
    } catch (error) {
      logger.error('Failed to read localStorage data', error);
      return undefined;
    }
    if (!storedDataString) return undefined;

    try {
      return JSON.parse(storedDataString) as T;
    } catch {
      logger.error('Failed to parse localStorage data');
      return undefined;
    }
  });

  const storeData = useCallback(
    (dataToSave: T) => {
      try {
        window.localStorage.setItem(key, JSON.stringify(dataToSave));
        setData(dataToSave);
      } catch (error) {
        logger.error('Failed to store data in localStorage', error);
      }
    },
    [key]
  );

  const removeStoredData = useCallback(() => {
    window.localStorage.removeItem(key);
    setData(undefined);
  }, [key]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === key) {
        try {
          setData(e.newValue ? JSON.parse(e.newValue) : undefined);
        } catch {
          setData(undefined);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [key]);

  return {
    data,
    storeData,
    removeStoredData,
  };
};
